import React, { useState, useEffect } from 'react'
import {
	Card,
	Form,
	Switch,
	Button,
	message,
	Spin,
	Typography,
	Divider,
	Row,
	Col,
	Tooltip,
	InputNumber,
} from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { httpGet, httpPost, httpPatch } from '../../common/http.service'

const { Text, Paragraph } = Typography

interface CleanerShiftConfig {
	'is-twice-check-in': boolean
	'morning-shift-bonus-minutes'?: number
}

const CleanerShiftTab: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [exists, setExists] = useState(false)
	const [editing, setEditing] = useState(false)
	const [initialValues, setInitialValues] = useState<CleanerShiftConfig>({
		'is-twice-check-in': false,
		'morning-shift-bonus-minutes': 0,
	})

	useEffect(() => {
		loadCleanerShift()
	}, [])

	const loadCleanerShift = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/config-shift/cleaner-shift')
			if (response?.data) {
				const values = response.data
				form.setFieldsValue(values)
				setInitialValues(values)
				setExists(true)
			}
		} catch (error) {
			console.error('Failed to load cleaner shift configuration:', error)
			setExists(false)
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (values: CleanerShiftConfig) => {
		try {
			if (exists) {
				await httpPatch('/api/v1/admin/config-shift/cleaner-shift', values)
				message.success('Cleaner shift configuration updated successfully')
			} else {
				await httpPost('/api/v1/admin/config-shift/cleaner-shift', values)
				message.success('Cleaner shift configuration created successfully')
				setExists(true)
			}
			setInitialValues(values)
			setEditing(false)
			loadCleanerShift()
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || 'Failed to save cleaner shift configuration',
			)
		}
	}

	return (
		<div>
			<Card
				title='Cleaner Shift Configuration'
				bordered={false}
				style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				extra={
					!editing && (
						<div style={{ display: 'flex', gap: '8px' }}>
							<Tooltip title='Edit button is enabled when configuration exists'>
								<Button
									type='primary'
									icon={<EditOutlined />}
									onClick={() => setEditing(true)}
									disabled={!exists}
								>
									Edit
								</Button>
							</Tooltip>
							<Tooltip title="Create button is enabled when there's no shift configuration">
								<Button
									type='primary'
									onClick={() => setEditing(true)}
									disabled={exists}
								>
									Create
								</Button>
							</Tooltip>
						</div>
					)
				}
			>
				<Spin spinning={loading}>
					{!editing && exists ? (
						<div>
							<Paragraph>
								Configure the cleaner shift check-in behavior for staff members.
							</Paragraph>
							<Divider />
							<Row gutter={[16, 16]}>
								<Col span={24}>
									<Text strong>Enable Twice Check-In</Text>
									<div style={{ marginTop: 8 }}>
										<Switch
											checked={form.getFieldValue('is-twice-check-in')}
											checkedChildren='Enabled'
											unCheckedChildren='Disabled'
											disabled
										/>
									</div>
								</Col>
								<Col span={24}>
									<Text strong>Morning Shift Bonus Minutes</Text>
									<div style={{ marginTop: 8 }}>
										<Text>
											{form.getFieldValue('morning-shift-bonus-minutes') ??
												initialValues['morning-shift-bonus-minutes'] ??
												'N/A'}{' '}
											minutes
										</Text>
									</div>
								</Col>
							</Row>
							<div
								style={{
									backgroundColor: '#f0f5ff',
									padding: '12px',
									borderRadius: '4px',
									marginTop: '16px',
								}}
							>
								<Text type='secondary' style={{ fontSize: '12px' }}>
									<strong>Note:</strong> When twice check-in is enabled, cleaner
									shift staff will need to check-in both at the start of their
									shift and after lunch break.
								</Text>
							</div>
						</div>
					) : !editing && !exists ? (
						<div style={{ textAlign: 'center', padding: '40px 0' }}>
							<Text type='secondary'>
								No cleaner shift configuration found. Click the Create button to set
								up.
							</Text>
						</div>
					) : (
						<div>
							<Paragraph>
								Configure the cleaner shift check-in behavior for staff members.
							</Paragraph>
							<Divider />

							<Form
								form={form}
								layout='vertical'
								onFinish={handleSubmit}
								initialValues={{
									'is-twice-check-in': false,
									'morning-shift-bonus-minutes': 0,
								}}
							>
								<Form.Item
									name='is-twice-check-in'
									label={<Text strong>Enable Twice Check-In</Text>}
									valuePropName='checked'
									tooltip='When enabled, staff must check-in twice per day'
								>
									<Switch
										checkedChildren='Enabled'
										unCheckedChildren='Disabled'
									/>
								</Form.Item>

								<Form.Item
									name='morning-shift-bonus-minutes'
									label={<Text strong>Morning Shift Bonus Minutes</Text>}
									rules={[{ type: 'number', min: 0 }]}
								>
									<InputNumber min={0} style={{ width: '100%' }} />
								</Form.Item>

								<div
									style={{
										backgroundColor: '#f0f5ff',
										padding: '12px',
										borderRadius: '4px',
										marginBottom: '16px',
									}}
								>
									<Text type='secondary' style={{ fontSize: '12px' }}>
										<strong>Note:</strong> When twice check-in is enabled,
										cleaner shift staff will need to check-in both at the start
										of their shift and after lunch break.
									</Text>
								</div>

								<Form.Item>
									<Row gutter={8}>
										<Col span={12}>
											<Button block onClick={() => setEditing(false)}>
												Cancel
											</Button>
										</Col>
										<Col span={12}>
											<Button type='primary' htmlType='submit' block>
												{exists ? 'Update' : 'Create'}
											</Button>
										</Col>
									</Row>
								</Form.Item>
							</Form>
						</div>
					)}
				</Spin>
			</Card>
		</div>
	)
}

export default CleanerShiftTab
